import { Box, Button, Text, VStack } from 'native-base'
import { useToast } from 'native-base'
import { useState } from 'react'

import { useMutation } from '@apollo/client'

import {
  AddressHours,
  AddressHoursInput,
  DayHours,
  DayHoursInput,
  DayOfWeek,
  UPDATE_ADDRESS_HOURS,
  UpdateAddressHoursMutation,
  UpdateAddressHoursMutationVariables,
} from '@consumer/graphql'
import { ToastAlert } from '../Instructions/Common/ToastAlert'
import DayHoursRow from './DayHoursRow'

interface EditBusinessHoursProps {
  normalizedAddressId: string
  orderId?: string
  initialHours: AddressHours
  onSave?: (hours: AddressHours) => void
  onCancel?: () => void
}

// Convert 12-hour format to 24-hour format (e.g., "8:00am" -> "08:00")
const format12To24Hour = (time12: string): string => {
  const match = /(\d+):(\d+)(am|pm)/.exec(time12)
  if (!match) return '0800'

  let hours = parseInt(match[1], 10)
  const minutes = match[2]
  const ampm = match[3]

  if (ampm === 'pm' && hours !== 12) hours += 12
  if (ampm === 'am' && hours === 12) hours = 0

  return `${hours.toString().padStart(2, '0')}${minutes}`
}

// Convert colon format to 4-digit format (e.g., "08:00" -> "0800")
const formatColonTo4Digit = (timeColon: string): string => {
  return timeColon.replace(':', '')
}

const dayOfWeekToNumber = (dayOfWeek: DayOfWeek): number => {
  switch (dayOfWeek) {
    case DayOfWeek.Sunday:
      return 0
    case DayOfWeek.Monday:
      return 1
    case DayOfWeek.Tuesday:
      return 2
    case DayOfWeek.Wednesday:
      return 3
    case DayOfWeek.Thursday:
      return 4
    case DayOfWeek.Friday:
      return 5
    case DayOfWeek.Saturday:
      return 6
    default:
      return 0
  }
}

const convertDayHoursToDayHoursInput = (dayHour: DayHours): DayHoursInput | undefined => {
  if (dayHour.isClosed) return undefined

  // Convert colon format to 4-digit format
  const openTime = dayHour.openTime ? formatColonTo4Digit(dayHour.openTime) : '0800'
  const closeTime = dayHour.closeTime ? formatColonTo4Digit(dayHour.closeTime) : undefined

  const openDay = dayOfWeekToNumber(dayHour.dayOfWeek)
  let closeDay = openDay
  if (openTime && closeTime && closeTime < openTime) {
    closeDay = (openDay + 1) % 7
  }

  const open = {
    day: openDay,
    time: openTime,
  }
  const close = closeTime
    ? {
        day: closeDay,
        time: closeTime,
      }
    : undefined

  return {
    open,
    close,
  }
}

// Generate time options in 30-minute increments
const generateTimeOptions = (): string[] => {
  const times: string[] = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = hour % 12 === 0 ? 12 : hour % 12
      const ampm = hour < 12 ? 'am' : 'pm'
      const m = minute.toString().padStart(2, '0')
      times.push(`${h}:${m}${ampm}`)
    }
  }
  return times
}

// Days of the week in order
const daysOfWeek: DayOfWeek[] = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
]

/** Form for editing business hours across all days of the week with save and cancel actions. */
export default function EditBusinessHours({
  normalizedAddressId,
  orderId,
  initialHours,
  onSave,
  onCancel,
}: EditBusinessHoursProps) {
  const { show: showToast, close: closeToast } = useToast()
  const [updateAddressHoursMutation, { loading }] = useMutation<
    UpdateAddressHoursMutation,
    UpdateAddressHoursMutationVariables
  >(UPDATE_ADDRESS_HOURS, {
    errorPolicy: 'all',
    onCompleted: () => {
      // trackEvent('update_address_hours_success', { features: 'cwa_business_hours' })
    },
  })

  // Initialize state with hours from initialHours or defaults
  const [dayHoursMap, setDayHoursMap] = useState<Map<DayOfWeek, DayHours>>(() => {
    const map = new Map<DayOfWeek, DayHours>()

    // First, set defaults for all days
    daysOfWeek.forEach(day => {
      map.set(day, {
        __typename: 'DayHours',
        dayOfWeek: day,
        openTime: '0800',
        closeTime: '1700',
        isClosed: false,
      })
    })

    // Then override with actual data if available
    if (initialHours.dayHours) {
      initialHours.dayHours.forEach(dayHour => {
        if (dayHour) {
          map.set(dayHour.dayOfWeek, { ...dayHour })
        }
      })
    }

    return map
  })

  const timeOptions = generateTimeOptions()

  const handleDayToggle = (day: DayOfWeek, isEnabled: boolean) => {
    const currentDayHours = dayHoursMap.get(day)
    if (currentDayHours) {
      setDayHoursMap(
        new Map(
          dayHoursMap.set(day, {
            ...currentDayHours,
            isClosed: !isEnabled,
          })
        )
      )
    }
  }

  const handleTimeChange = (day: DayOfWeek, field: 'openTime' | 'closeTime', value: string) => {
    const currentDayHours = dayHoursMap.get(day)
    if (currentDayHours) {
      setDayHoursMap(
        new Map(
          dayHoursMap.set(day, {
            ...currentDayHours,
            [field]: format12To24Hour(value),
          })
        )
      )
    }
  }

  const handleSave = async () => {
    const updatedHours: DayHoursInput[] = Array.from(dayHoursMap.values())
      .map(convertDayHoursToDayHoursInput)
      .filter((dayHours): dayHours is DayHoursInput => dayHours !== undefined)

    const input: AddressHoursInput = {
      dayHours: updatedHours,
      normalizedAddressId,
      orderId,
    }
    let addressHours: AddressHours | undefined
    try {
      const result = await updateAddressHoursMutation({ variables: { addressHoursInput: input } })
      addressHours = result?.data?.updateAddressHours ?? undefined
    } catch (error) {
      console.error('Error saving business hours:', error)
      // Show a error toast
      showToast({
        placement: 'top',
        render: ({ id }) => (
          <ToastAlert
            id={id}
            title={'Error updating business hours'}
            description={'Sorry, something went wrong updating your business hours. Please try again.'}
            status={'error'}
            isClosable={true}
            closeToast={closeToast}
          />
        ),
      })
    }

    if (onSave && addressHours) {
      // Show a success toast
      showToast({
        placement: 'top',
        render: ({ id }) => (
          <ToastAlert
            id={id}
            title={'Business hours updated'}
            description={'Your business hours have been updated'}
            status={'success'}
            isClosable={true}
            closeToast={closeToast}
          />
        ),
      })
      onSave(addressHours)
    }
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  return (
    <Box width="100%" maxW="600px" mx="auto">
      <VStack space={3} alignItems="center">
        <Text fontSize="lg" fontWeight="bold" textAlign="center">
          Edit Business Hours
        </Text>
        <Text fontSize="sm" textAlign="center" color="gray.600">
          Select the days and hours your location is open.
        </Text>

        <VStack space={2} alignItems="center" alignSelf="center">
          {daysOfWeek.map(day => {
            const dayHour = dayHoursMap.get(day)
            if (!dayHour) return null

            return (
              <DayHoursRow
                key={day}
                dayHours={dayHour}
                onToggle={isEnabled => handleDayToggle(day, isEnabled)}
                onOpenTimeChange={value => handleTimeChange(day, 'openTime', value)}
                onCloseTimeChange={value => handleTimeChange(day, 'closeTime', value)}
                timeOptions={timeOptions}
              />
            )
          })}

          {/* Disclaimer */}
          <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
            We guarantee no deliveries on your "Closed" days. However, delivery times cannot be guaranteed and may
            occasionally fall outside your stated business hours.
          </Text>

          {/* Save button */}
          <Button onPress={handleSave} size="lg" mt={4} alignSelf="stretch" isLoading={loading}>
            Save & Exit
          </Button>

          {/* Cancel button */}
          <Button size="lg" variant="ghost" _text={{ color: 'black' }} onPress={handleCancel} alignSelf="stretch">
            Cancel
          </Button>
        </VStack>
      </VStack>
    </Box>
  )
}
